(function () {
  var SECTION_SELECTOR = '.oddit-section-pdp-ingredients';
  var sectionState = new Map();

  // Mouse drag-to-scroll for a native scroller (Model A): the drag moves
  // scrollLeft and nothing else. Touch is left alone — it already scrolls.
  // `onSettle` runs on release after an actual drag, for scrollers that snap.
  function enableMouseDrag(scroller, signal, onSettle) {
    var dragActive = false;
    var dragMoved = false;
    var startX = 0;
    var lastX = 0;

    // Native drag-and-drop on images/links steals the gesture: the browser
    // starts its own drag and stops sending pointermove, so the strip sticks.
    scroller.addEventListener('dragstart', function (e) {
      e.preventDefault();
    }, { signal: signal });

    scroller.addEventListener('pointerdown', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      if (typeof e.button === 'number' && e.button !== 0) return;
      // Nothing overflows — leave it be, so the grab cursor never lies.
      if (scroller.scrollWidth <= scroller.clientWidth) return;
      dragActive = true;
      dragMoved = false;
      startX = e.clientX;
      lastX = e.clientX;
      scroller.classList.add('is-dragging');
    }, { signal: signal });

    window.addEventListener('pointermove', function (e) {
      if (!dragActive) return;
      var dx = e.clientX - lastX;
      lastX = e.clientX;
      if (Math.abs(e.clientX - startX) > 4) dragMoved = true;
      scroller.scrollLeft -= dx;
    }, { signal: signal });

    function endDrag() {
      if (!dragActive) return;
      dragActive = false;
      scroller.classList.remove('is-dragging');
      if (dragMoved && onSettle) onSettle();
    }
    window.addEventListener('pointerup', endDrag, { signal: signal });
    window.addEventListener('pointercancel', endDrag, { signal: signal });

    // Swallow the click that ends a drag, so dragging never activates a
    // card or a tab.
    scroller.addEventListener('click', function (e) {
      if (dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        dragMoved = false;
      }
    }, { capture: true, signal: signal });
  }

  // Model A (native scroll): position lives in viewport.scrollLeft only.
  // Finite strip — no clones, no loop — so there is nothing to normalize at rest.
  // Operates on VISIBLE cards only; hidden (non-active tab) cards are excluded so
  // tab switching recomputes pages/dots for the shown set. Returns { rebuild }.
  function initCarousel(section, signal, state) {
    var viewport = section.querySelector('.oddit-section-pdp-ingredients__cards-viewport');
    var dotsWrap = section.querySelector('.oddit-section-pdp-ingredients__dots');
    var allCards = Array.prototype.slice.call(
      section.querySelectorAll('.oddit-section-pdp-ingredients__card')
    );
    if (!viewport || !allCards.length) return { rebuild: function () {} };

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function visibleCards() {
      return allCards.filter(function (c) { return !c.hidden; });
    }

    // Scan real offsets — never assume a fixed/rounded pitch.
    function posOf(list, i) {
      return list[i].offsetLeft - list[0].offsetLeft;
    }
    function maxScroll() {
      return viewport.scrollWidth - viewport.clientWidth;
    }
    // Where a released drag should land: the nearest snap position. Those are
    // the card starts PLUS the scroll end (the last card is end-aligned), or the
    // strip could never rest on its trailing gutter.
    function settlePos() {
      var list = visibleCards();
      var max = maxScroll();
      var best = max;
      var bestDist = Math.abs(max - viewport.scrollLeft);
      for (var i = 0; i < list.length; i++) {
        var pos = Math.min(posOf(list, i), max);
        var dist = Math.abs(pos - viewport.scrollLeft);
        if (dist < bestDist) {
          bestDist = dist;
          best = pos;
        }
      }
      return best;
    }

    // A page is a whole number of CARDS — as many as fit at once — not a raw
    // viewport width. Scrolling snaps to card starts, so measuring pages in
    // viewport widths puts page boundaries mid-card.
    function cardsPerPage(list) {
      var style = getComputedStyle(viewport);
      var inner = viewport.clientWidth -
        parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      var cardW = list[0].getBoundingClientRect().width;
      var fit = 0;
      for (var i = 0; i < list.length; i++) {
        if (posOf(list, i) + cardW <= inner + 0.5) fit++;
      }
      return Math.max(1, fit);
    }

    // Scroll position of each page — every one is a real card start (clamped to
    // the scroll end, which the last card is snap-aligned to).
    function pageStarts() {
      var list = visibleCards();
      if (!list.length) return [0];
      var max = maxScroll();
      var per = cardsPerPage(list);
      var starts = [];
      for (var i = 0; i < list.length; i += per) {
        var pos = Math.min(posOf(list, i), max);
        // Trailing pages can clamp onto the same end position — one dot for it.
        if (!starts.length || pos - starts[starts.length - 1] > 1) starts.push(pos);
      }
      return starts;
    }

    function activePage() {
      var starts = pageStarts();
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < starts.length; i++) {
        var dist = Math.abs(starts[i] - viewport.scrollLeft);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    }

    function scrollToPage(i) {
      var starts = pageStarts();
      viewport.scrollTo({
        left: starts[Math.max(0, Math.min(i, starts.length - 1))],
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
      updateActiveDot();
    }

    function buildDots() {
      if (!dotsWrap) return;
      var count = pageStarts().length;
      dotsWrap.textContent = '';
      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'oddit-section-pdp-ingredients__dot';
        dot.setAttribute('aria-label', 'Go to page ' + (i + 1) + ' of ' + count);
        dot.addEventListener('click', function (e) {
          scrollToPage(Array.prototype.indexOf.call(dotsWrap.children, e.currentTarget));
        }, { signal: signal });
        dotsWrap.appendChild(dot);
      }
      // A single page means nothing to paginate.
      dotsWrap.hidden = count < 2;
    }

    function updateActiveDot() {
      if (!dotsWrap) return;
      var active = activePage();
      Array.prototype.forEach.call(dotsWrap.children, function (dot, i) {
        dot.classList.toggle('oddit-section-pdp-ingredients__dot--active', i === active);
        dot.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }

    // Live dot sync, coalesced to one layout read per frame.
    viewport.addEventListener('scroll', function () {
      if (state.raf) return;
      state.raf = requestAnimationFrame(function () {
        state.raf = null;
        updateActiveDot();
      });
    }, { passive: true, signal: signal });

    // Cards settle per card on release — only the dots count pages.
    enableMouseDrag(viewport, signal, function () {
      viewport.scrollTo({
        left: settlePos(),
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
      updateActiveDot();
    });

    // Card widths change per breakpoint, and again once fonts/images land —
    // both change how many pages there are, so rebuild rather than just resync.
    state.observer = new ResizeObserver(function () {
      buildDots();
      updateActiveDot();
    });
    state.observer.observe(viewport);

    buildDots();
    updateActiveDot();

    // Called after a tab swap changes the visible card set.
    return {
      rebuild: function () {
        viewport.scrollLeft = 0;
        buildDots();
        updateActiveDot();
      }
    };
  }

  function initTabs(section, signal, onSelect) {
    var tabsWrap = section.querySelector('.oddit-section-pdp-ingredients__tabs');
    var tabs = Array.prototype.slice.call(
      section.querySelectorAll('.oddit-section-pdp-ingredients__tab')
    );

    // The tab row only overflows once the labels stop fitting; when it does,
    // it drags like the card carousel. enableMouseDrag no-ops while it fits.
    if (tabsWrap) enableMouseDrag(tabsWrap, signal);

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) {
          t.classList.remove('oddit-section-pdp-ingredients__tab--active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('oddit-section-pdp-ingredients__tab--active');
        tab.setAttribute('aria-selected', 'true');
        // Centre the clicked tab in the row. Clamps itself at the two ends, and
        // no-ops while the row still fits. block: 'nearest' keeps it from
        // scrolling the page vertically.
        tab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        if (onSelect) onSelect(tab.getAttribute('data-tab'));
      }, { signal: signal });
    });
  }

  function initSection(section) {
    if (sectionState.has(section)) return;
    var state = { controller: new AbortController(), raf: null, observer: null };
    sectionState.set(section, state);

    var carousel = initCarousel(section, state.controller.signal, state);
    var cards = Array.prototype.slice.call(
      section.querySelectorAll('.oddit-section-pdp-ingredients__card')
    );

    // Swap the visible card set to the clicked tab, then rebuild the carousel.
    initTabs(section, state.controller.signal, function (slug) {
      cards.forEach(function (card) {
        card.hidden = card.getAttribute('data-parent-tab') !== slug;
      });
      carousel.rebuild();
    });
  }

  function destroySection(section) {
    var state = sectionState.get(section);
    if (!state) return;
    state.controller.abort();
    if (state.raf) cancelAnimationFrame(state.raf);
    if (state.observer) state.observer.disconnect();
    sectionState.delete(section);
  }

  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();
